import { useEffect, useState } from "react";
import { Loader2, ImageIcon, Plus, Trash2, Shield, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import PageHeader from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const Settings = () => {
  const { isAdmin, user } = useAuth();
  const { settings, setSettings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // users
  const [users, setUsers] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [uSaving, setUSaving] = useState(false);
  const [delUser, setDelUser] = useState(null);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  useEffect(() => { if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isAdmin]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { toast.error("Logo maks 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setSettings(data);
      refresh();
      toast.success("Pengaturan disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async () => {
    if (!uForm.name || !uForm.email || !uForm.password) { toast.error("Lengkapi data pengguna"); return; }
    setUSaving(true);
    try {
      await api.post("/auth/users", uForm);
      toast.success("Pengguna ditambahkan");
      setUOpen(false);
      setUForm({ name: "", email: "", password: "", role: "staff" });
      const r = await api.get("/auth/users");
      setUsers(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUSaving(false);
    }
  };

  const doDeleteUser = async () => {
    try {
      await api.delete(`/auth/users/${delUser}`);
      toast.success("Pengguna dihapus");
      setUsers((u) => u.filter((x) => x.id !== delUser));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDelUser(null);
    }
  };

  if (!form) return null;

  return (
    <div className="space-y-6">
      <PageHeader title="Pengaturan" subtitle="Konfigurasi usaha dan pengguna" />

      <Tabs defaultValue="business">
        <TabsList>
          <TabsTrigger value="business" data-testid="tab-business">Profil Usaha</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>}
        </TabsList>

        <TabsContent value="business">
          <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary">
                {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-cover" /> : <ImageIcon className="h-7 w-7 text-muted-foreground" />}
              </div>
              <div>
                <Label htmlFor="logo" className="cursor-pointer text-sm font-medium text-primary hover:underline">Upload Logo Usaha</Label>
                <input id="logo" type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
                <p className="text-xs text-muted-foreground">PNG/JPG, maks 1MB</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Nama Usaha</Label><Input value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="business-name-input" /></div>
              <div className="space-y-2"><Label>No. WhatsApp</Label><Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="business-wa-input" /></div>
              <div className="space-y-2"><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>PPN Default (%)</Label><Input type="number" value={form.tax_default ?? 0} onChange={(e) => setForm({ ...form, tax_default: Number(e.target.value) })} data-testid="tax-default-input" /></div>
              <div className="space-y-2"><Label>Mata Uang</Label>
                <Select value={form.currency || "IDR"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="IDR">Rupiah (IDR)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2"><Label>Alamat Usaha</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Catatan Default Invoice</Label><Textarea value={form.invoice_note || ""} onChange={(e) => setForm({ ...form, invoice_note: e.target.value })} rows={2} /></div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={saveSettings} disabled={saving || !isAdmin} data-testid="save-settings-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan Pengaturan
              </Button>
            </div>
            {!isAdmin && <p className="mt-2 text-right text-xs text-muted-foreground">Hanya admin yang dapat mengubah pengaturan.</p>}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users">
            <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
              <div className="flex items-center justify-between p-4">
                <h3 className="font-heading font-semibold">Daftar Pengguna</h3>
                <Button size="sm" onClick={() => setUOpen(true)} data-testid="add-user-btn"><Plus className="mr-2 h-4 w-4" />Tambah Pengguna</Button>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium capitalize">
                          {u.role === "admin" ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}{u.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        {u.id !== user?.id && (
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDelUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nama</Label><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUOpen(false)}>Batal</Button>
            <Button onClick={saveUser} disabled={uSaving} data-testid="save-user-btn">{uSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delUser} onOpenChange={() => setDelUser(null)} title="Hapus Pengguna?" description="Pengguna ini tidak akan bisa login lagi." onConfirm={doDeleteUser} />
    </div>
  );
};

export default Settings;
